interface CartProduct {
  quantity: number;
  id: string;
  title: string;
  subtitle: string;
  thumb: string;
  link: string;
  price_usd: number;
  price_cad: number;
}

interface RootDataset {
  id: string;
  title: string;
  subtitle: string;
  thumb: string;
  link: string;
  price: string;
}

export interface RootContainer {
  dataset: RootDataset;
  id: string | undefined;
}

enum EventType {
  Increment = "Increment",
  Decrement = "Decrement"
}

interface Price {
  usd: number;
  cad: number;
}

type CartCounter = { [key: string]: number };

const splitPriceCadUsd = (priceString: string): Price => {
  let prices = priceString.split('/');

  if (prices.length == 1) {
    return {
      usd: parseInt(priceString),
      cad: parseInt(priceString)
    }
  }

  if (prices.length < 1 ) {
    return {
      usd: 0,
      cad: 0
    }
  }

  return {
    usd: parseInt(prices[0].replace(/\D/g, '')),
    cad: parseInt(prices[1].replace(/\D/g, ''))
  }
}

export class CartStore {
  storageName: string;
  products: CartProduct[];
  counter: CartCounter;
  cart_total: number;
  cart_amount: Price;
  isInit: boolean;
  isCartDialog: boolean;

  constructor(key: string, persistFn: (obj: any, key: string) => any) {
    this.storageName = key; // set storage key name

    this.products = persistFn([], key);

    this.counter = {};
    this.cart_total = 0;
    this.cart_amount = {usd: 0, cad: 0};
    this.isInit = false;

    this.isCartDialog = persistFn(false, "isCartDialog");
  }

  updateStore(event: EventType, product: CartProduct) {
    if (event === EventType.Increment) {
      // update quantity
      product.quantity++;
      // update cart_total
      this.cart_total++;
    } else if (event === EventType.Decrement) {
      // update quantity
      product.quantity--;
      // update cart_total
      this.cart_total--;
    }
    // update counter
    this.counter[product.id] = product.quantity;
    // update cart_amount
    this.cart_amount = this.getCartAmount();
  }

  saveProduct(product: CartProduct) {
    // add product to products list
    this.products.push(product);
  }

  createProduct(root: RootContainer): CartProduct {
    let price = splitPriceCadUsd(root.dataset.price);
    return {
      id: root.dataset.id,
      title: root.dataset.title,
      subtitle: root.dataset.subtitle,
      thumb: root.dataset.thumb,
      link: root.dataset.link,
      price_usd: price.usd,
      price_cad: price.cad,
      quantity: 0
    };
  }

  add(root: RootContainer) {
    // ref product id
    const id = root.id || root.dataset.id;
    // get product
    let product = this.getProductById(id);

    if (!product) {
      // create product
      product = this.createProduct(root);
      // save product
      this.saveProduct(product);
      // update store
      this.updateStore(EventType.Increment, product);
    } else if (product.quantity < 10) {
      // update store
      this.updateStore(EventType.Increment, product);
    }
  }

  // delete all instances of this product
  delete(id: string) {
    // ref product id
    const isProductToDelete = this.products.find(
      (product) => product.id === id,
    );

    if (isProductToDelete) {
      // remove product from counter
      delete this.counter[isProductToDelete.id];
      // remove product quantity from cart_total
      this.cart_total = this.cart_total - isProductToDelete.quantity;
      // reset product quatity
      isProductToDelete.quantity = 0;
      // update cart amount
      this.cart_amount = this.getCartAmount();
      // update cart products list
      this.products = this.products.filter((product) => product.id !== id);
    } else {
      // eslint-disable-next-line no-useless-return
      return;
    }
  }

  clear() {
    // reset store values
    this.counter = {};
    this.products = [];
    this.cart_total = 0;
    this.cart_amount = { usd: 0, cad: 0 };
  }

  remove(root: RootContainer) {
    // ref product id
    const id = root.id || root.dataset.id;
    // get product
    const product = this.getProductById(id);
    if (product) {
      if (product.quantity > 1) {
        this.updateStore(EventType.Decrement, product);
      } else {
        // remove product
        this.delete(id);
      }
    }
  }

  getProductById(id: string): CartProduct | undefined {
    const isProduct = this.products.filter((product) => product.id === id);
    return isProduct[0];
  }

  getCartTotal(): number {
    const initialValue = 0;
    const totalQuantity = this.products.reduce(
      (acc, cur) => acc + cur.quantity,
      initialValue,
    );
    return totalQuantity;
  }

  getCartAmount(): Price {
    const initialValue = {usd: 0, cad: 0};

    return this.products.reduce(
      (acc, cur) => {
        acc.usd = acc.usd + cur.price_usd * cur.quantity;
        acc.cad = acc.cad + cur.price_cad * cur.quantity;
        return acc;
      },
      initialValue,
    );
  }

  setCartCounter(): CartCounter {
    const counter: CartCounter = {};
    this.products.map((product) => counter[product.id] === product.quantity);
    return counter;
  }

  _init() {
    if (!this.isInit) {
      this.cart_total = this.getCartTotal();
      this.cart_amount = this.getCartAmount();
      this.counter = this.setCartCounter();
      this.isInit = true;
    }
  }

  init() {
    // reload opend tabs
    window.addEventListener("storage", () => location.reload());
  }

  async checkout() {
    let post_items = this.products.map((item: CartProduct) => {
      return { id: item.id, quantity: item.quantity };
    });

    let response = await fetch("/store/checkout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items: post_items }),
    });

    const result = await response.json();

    if (result.session_url) {
      window.location.href = result.session_url;
    }
  }
}
